import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Package,
  TrendingUp,
  DollarSign,
  Users,
  ShoppingCart,
  AlertTriangle,
  Star,
  ArrowUp,
  ArrowDown,
  Calendar,
  BarChart3,
  RefreshCw,
  Plus,
  Download,
  EyeOff,
  Eye,
  Edit2,
  Truck,
  MessageCircle,
  Settings,
  CreditCard
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Helper: extract a readable string from { en, hi, es, zh } or plain string
const getLocalizedString = (value, fallback = '') => {
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    // Prefer English, then first available language
    return value.en || value.hi || value.es || value.zh || Object.values(value)[0] || fallback;
  }
  return fallback;
};

const SupplierDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    outOfStock: 0,
    inactiveProducts: 0,
    bestProducts: 0,
    totalRevenue: 0,
    monthlySales: 0
  });
  const [recentProducts, setRecentProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');

  // Mock data for dashboard cards
  const dashboardData = {
    performance: {
      rating: 4.8,
      orders: 45,
      responseTime: '2.4h',
      fulfillment: 98.7
    },
    quickStats: [
      { label: 'Pending Orders', value: 12, color: 'orange' },
      { label: 'Messages', value: 8, color: 'blue' },
      { label: 'To Ship', value: 5, color: 'purple' }
    ]
  };

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const supplierId = user?.supplierId || user?.supplier?._id || user?._id;

      // Fetch products count
      const countResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/products/supplier-products-count/${supplierId}`
      );

      // Fetch all products to calculate inactive count
      const productsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/products/products-by-supplier/${supplierId}`
      );

      // Calculate inactive products
      const allProducts = productsResponse.data.products || [];
      const inactiveProducts = allProducts.filter(product => product.stock_status === "inactive").length;

      setStats({
        totalProducts: countResponse.data.counts.total,
        activeProducts: countResponse.data.counts.active,
        outOfStock: countResponse.data.counts.outOfStock,
        inactiveProducts: inactiveProducts, 
        bestProducts: 0,
        totalRevenue: 125000,
        monthlySales: 45
      });

      // Normalize product names and categories for display
      const normalizedProducts = allProducts.slice(0, 5).map(p => ({
        ...p,
        displayName: getLocalizedString(p.name, 'Unnamed'),
        categoryName: getLocalizedString(p.category?.name, '--')
      }));
      setRecentProducts(normalizedProducts);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, change, color, onClick }) => (
    <div
      className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-300 cursor-pointer ${onClick ? 'hover:scale-105' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{loading ? '...' : value}</p>
          {change && (
            <div className={`flex items-center mt-2 text-sm ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change > 0 ? <ArrowUp className="w-4 h-4 mr-1" /> : <ArrowDown className="w-4 h-4 mr-1" />}
              {Math.abs(change)}% from last month
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const QuickAction = ({ icon: Icon, title, description, color, onClick }) => (
    <div
      className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-all duration-300 cursor-pointer hover:border-blue-300 group"
      onClick={onClick}
    >
      <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );

  const MiniStatCard = ({ label, value, color }) => (
    <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
      <div className={`w-3 h-3 rounded-full bg-${color}-500 mx-auto mb-2`}></div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-600">{label}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-xl p-6 h-32"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.businessName || user?.fullName || 'Seller'}! 👋
          </h1>
          <p className="text-gray-600">Here's what's happening with your store today.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 items-center gap-3 mt-4 lg:mt-0">
          <select 
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
          </select>
          <button
            onClick={fetchDashboardData}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => navigate('/seller/add-product')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Add Product
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Products"
          value={stats.totalProducts}
          icon={Package}
          change={12}
          color="bg-blue-500"
          onClick={() => navigate('/seller/products')}
        />
        <StatCard
          title="Active Products"
          value={stats.activeProducts}
          icon={TrendingUp}
          change={8}
          color="bg-green-500"
        />
        <StatCard
          title="Out of Stock"
          value={stats.outOfStock}
          icon={AlertTriangle}
          change={-5}
          color="bg-red-500"
        />
        <StatCard
          title="Inactive Products"
          value={stats.inactiveProducts}
          icon={EyeOff}
          change={15}
          color="bg-yellow-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Quick Stats Row */}
          <div className="grid grid-cols-3 gap-4">
            <MiniStatCard label="Pending Orders" value="12" color="orange" />
            <MiniStatCard label="Messages" value="8" color="blue" />
            <MiniStatCard label="To Ship" value="5" color="purple" />
          </div>

          {/* Revenue Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Revenue Overview</h2>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="w-4 h-4" />
                This Month
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                <DollarSign className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <p className="text-2xl font-bold text-gray-900">₹{stats.totalRevenue.toLocaleString()}</p>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <div className="flex items-center justify-center mt-2 text-green-600 text-sm">
                  <ArrowUp className="w-4 h-4 mr-1" />
                  12% increase
                </div>
              </div>
              <div className="text-center p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                <ShoppingCart className="w-8 h-8 text-green-600 mx-auto mb-3" />
                <p className="text-2xl font-bold text-gray-900">{stats.monthlySales}</p>
                <p className="text-sm text-gray-600">Monthly Sales</p>
                <div className="flex items-center justify-center mt-2 text-green-600 text-sm">
                  <ArrowUp className="w-4 h-4 mr-1" />
                  8% increase
                </div>
              </div>
            </div>
          </div>

          {/* Recent Products - Enhanced */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Recent Products</h2>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/seller/products')}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  View All
                </button>
                <button className="flex items-center gap-2 px-3 py-1 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                  <Download className="w-4 h-4" />
                  Export
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Product</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Category</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Price</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Stock</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentProducts.length > 0 ? (
                    recentProducts.map((product) => (
                      <tr key={product._id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <img
                              src={product.images?.[0] || '/placeholder-image.jpg'}
                              alt={product.displayName}
                              className="w-10 h-10 object-cover rounded-lg border"
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-900 line-clamp-1">{product.displayName}</p>
                              <p className="text-xs text-gray-500">{product.bhkg_id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {product.categoryName}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          ₹{product.price}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            product.stock_status === 'active' 
                              ? 'bg-green-100 text-green-800'
                              : product.stock_status === 'out of stock'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {product.stock_status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/seller/product-details/${product._id}`)}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded transition"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => navigate(`/seller/edit-product/${product._id}`)}
                              className="p-1 text-green-600 hover:bg-green-50 rounded transition"
                              title="Edit Product"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <Package className="w-12 h-12 text-gray-300" />
                          <p className="text-gray-500">No products yet</p>
                          <button
                            onClick={() => navigate('/seller/add-product')}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            Add your first product
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-8">
          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 gap-4">
              <QuickAction
                icon={Plus}
                title="Add New Product"
                description="List a new product in your catalog"
                color="bg-blue-500"
                onClick={() => navigate('/seller/add-product')}
              />
              <QuickAction
                icon={Package}
                title="Manage Products"
                description="View and edit your product listings"
                color="bg-green-500"
                onClick={() => navigate('/seller/products')}
              />
              <QuickAction
                icon={Truck}
                title="Order Management"
                description="Process and track orders"
                color="bg-orange-500"
                onClick={() => navigate('/seller/orders')}
              />
              <QuickAction
                icon={MessageCircle}
                title="Customer Messages"
                description="Respond to customer queries"
                color="bg-purple-500"
                onClick={() => navigate('/seller/messages')}
              />
            </div>
          </div>

          {/* Store Performance */}
          <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl text-white p-6">
            <h3 className="font-semibold mb-4 text-lg">Store Performance</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-blue-100">Store Rating</span>
                <span className="font-semibold flex items-center gap-1">
                  {dashboardData.performance.rating}/5 <Star className="w-4 h-4 fill-current" />
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-blue-100">Total Orders</span>
                <span className="font-semibold">{dashboardData.performance.orders}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-blue-100">Response Time</span>
                <span className="font-semibold">{dashboardData.performance.responseTime}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-blue-100">Fulfillment Rate</span>
                <span className="font-semibold">{dashboardData.performance.fulfillment}%</span>
              </div>
            </div>
            <button className="w-full mt-6 bg-white text-blue-600 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors shadow-sm">
              View Performance Details
            </button>
          </div>

          {/* Inventory Health */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Inventory Health</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="text-green-700 font-medium">Active</span>
                <span className="text-green-700 font-bold">{stats.activeProducts}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg border border-red-200">
                <span className="text-red-700 font-medium">Out of Stock</span>
                <span className="text-red-700 font-bold">{stats.outOfStock}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                <span className="text-gray-700 font-medium">Inactive</span>
                <span className="text-gray-700 font-bold">{stats.inactiveProducts}</span>
              </div>
            </div>
          </div>

          {/* Support Quick Links */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Support & Resources</h3>
            <div className="space-y-3">
              <button className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">Payment Settings</span>
              </button>
              <button className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-3">
                <Settings className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">Account Settings</span>
              </button>
              <button className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700">Help Center</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Tips Section */}
      <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Seller Tips</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2">💡 Boost Visibility</h3>
            <p className="text-sm text-blue-700">Add high-quality images and detailed descriptions to increase product views.</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-2">🚀 Quick Sales</h3>
            <p className="text-sm text-green-700">Offer competitive pricing and fast shipping to improve conversion rates.</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-2">⭐ Build Reputation</h3>
            <p className="text-sm text-purple-700">Respond to customer queries quickly to maintain high seller ratings.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierDashboard;